import Navbar from "@layout/Navbar";
import { apiSendContactMessage } from "@services/api";
import { useEffect, useState } from "react";

const FIELDS = [
  { key: "name",    label: "Your Name",    type: "text",  placeholder: "Enter your full name",    required: true  },
  { key: "email",   label: "Email",        type: "email", placeholder: "Enter your email address", required: true  },
  { key: "phone",   label: "Phone",        type: "text",  placeholder: "Enter your phone number",  required: false },
  { key: "message", label: "Message",      type: "area",  placeholder: "What can we do for you?",  required: true  },
];

const validate = (key, value) => {
  if (key === "name"    && !value.trim())          return "Full name is required";
  if (key === "email"   && !value.trim())          return "Email address is required";
  if (key === "email"   && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Enter a valid email address";
  if (key === "message" && !value.trim())          return "Please write a message";
  if (key === "message" && value.trim().length < 10) return "Message must be at least 10 characters";
  return "";
};

export default function ContactPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [form,    setForm]    = useState({ name: "", email: "", phone: "", message: "" });
  const [errors,  setErrors]  = useState({});
  const [touched, setTouched] = useState({});
  const [busy,    setBusy]    = useState(false);
  const [sent,    setSent]    = useState(false);
  const [submitError, setSubmitError] = useState("");

  const setField = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (touched[k]) setErrors(e => ({ ...e, [k]: validate(k, v) }));
  };

  const blur = (k) => {
    setTouched(t => ({ ...t, [k]: true }));
    setErrors(e => ({ ...e, [k]: validate(k, form[k]) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    const allTouched = {};
    FIELDS.forEach(f => { newErrors[f.key] = validate(f.key, form[f.key]); allTouched[f.key] = true; });
    setErrors(newErrors);
    setTouched(allTouched);
    if (Object.values(newErrors).some(Boolean)) return;
    setBusy(true);
    setSubmitError("");
    const res = await apiSendContactMessage(form);
    setBusy(false);
    if (res?.status === 1) {
      setSent(true);
    } else {
      // TEMP: appending res.debug while diagnosing the send failure - remove once resolved
      const base = res?.message || "Something went wrong. Please try again, or email us directly.";
      setSubmitError(res?.debug ? `${base} (${res.debug})` : base);
    }
  };

  const hasError = (k) => touched[k] && errors[k];
  const isOk     = (k) => touched[k] && !errors[k] && form[k];

  return (
    <div style={{ minHeight: "100vh", background: "#F3F4F6" }}>
      <Navbar solid />

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "100px 24px 80px" }}>
        <div className="row g-5 align-items-start">

          {/* ── Left: info panel ── */}
          <div className="col-12 col-lg-5">
            <div style={{ position: "sticky", top: 90 }}>

              {/* Tag */}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 99, padding: "5px 14px", marginBottom: 20 }}>
                <i className="fa-solid fa-envelope" style={{ color: "#2563EB", fontSize: 11 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#2563EB", letterSpacing: "0.05em", textTransform: "uppercase" }}>Get In Touch</span>
              </div>

              <h1 style={{ fontSize: "clamp(30px,4vw,44px)", fontWeight: 800, color: "#111827", marginBottom: 12, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                Contact Us
              </h1>
              <p style={{ fontSize: 16, color: "#6B7280", lineHeight: 1.75, marginBottom: 6 }}>
                Questions? Let's talk!
              </p>
              <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.75, marginBottom: 36 }}>
                We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>

              {/* Contact info cards */}
              {[
                { icon: "fa-envelope",      color: "#2563EB", bg: "#EFF6FF", title: "Email us",       value: "info@firmconnector.com", link: "mailto:info@firmconnector.com" },
                { icon: "fa-clock",         color: "#16A34A", bg: "#F0FDF4", title: "Response time",  value: "Within 24 hours",         link: null },
                { icon: "fa-location-dot",  color: "#F59E0B", bg: "#FEF3C7", title: "Location",       value: "Halifax, Nova Scotia, Canada", link: null },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 14, marginBottom: 16 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: item.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${item.color}22` }}>
                    <i className={`fa-solid ${item.icon}`} style={{ color: item.color, fontSize: 17 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2 }}>{item.title}</div>
                    {item.link
                      ? <a href={item.link} style={{ fontSize: 14, fontWeight: 600, color: "#2563EB", textDecoration: "none" }}>{item.value}</a>
                      : <div style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>{item.value}</div>
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: Form ── */}
          <div className="col-12 col-lg-7">
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 24, padding: "40px 36px", boxShadow: "0 4px 32px rgba(0,0,0,0.06)" }}>

              {sent ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(135deg, #16A34A, #06B6D4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 8px 24px rgba(16,185,129,0.3)" }}>
                    <i className="fa-solid fa-check" style={{ color: "#fff", fontSize: 28 }} />
                  </div>
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 10 }}>Message sent!</h3>
                  <p style={{ fontSize: 15, color: "#6B7280", marginBottom: 28 }}>We'll get back to you within 24 hours.</p>
                  <button onClick={() => { setSent(false); setForm({ name:"", email:"", phone:"", message:"" }); setTouched({}); setErrors({}); }}
                    style={{ padding: "10px 24px", borderRadius: 99, background: "#EFF6FF", color: "#2563EB", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 4 }}>Send us a message</h2>
                  <p style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 28 }}>Fill in the form below and we'll be in touch shortly</p>

                  <div className="row g-3">

                    {/* Name + Email side by side */}
                    <div className="col-12 col-sm-6">
                      <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                        Your Name <span style={{ color: "#DC2626" }}>*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter your full name"
                        value={form.name}
                        onChange={e => setField("name", e.target.value)}
                        onBlur={() => blur("name")}
                        style={{ borderColor: hasError("name") ? "#DC2626" : isOk("name") ? "#16A34A" : "#E5E7EB", boxShadow: hasError("name") ? "0 0 0 3px rgba(239,68,68,0.1)" : isOk("name") ? "0 0 0 3px rgba(16,185,129,0.1)" : "none" }}
                      />
                      {hasError("name") && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: 12, color: "#DC2626" }}>
                          <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} />{errors.name}
                        </div>
                      )}
                      {isOk("name") && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: 12, color: "#16A34A" }}>
                          <i className="fa-solid fa-circle-check" style={{ fontSize: 11 }} />Looks good!
                        </div>
                      )}
                    </div>

                    <div className="col-12 col-sm-6">
                      <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                        Email <span style={{ color: "#DC2626" }}>*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Enter your email address"
                        value={form.email}
                        onChange={e => setField("email", e.target.value)}
                        onBlur={() => blur("email")}
                        style={{ borderColor: hasError("email") ? "#DC2626" : isOk("email") ? "#16A34A" : "#E5E7EB", boxShadow: hasError("email") ? "0 0 0 3px rgba(239,68,68,0.1)" : isOk("email") ? "0 0 0 3px rgba(16,185,129,0.1)" : "none" }}
                      />
                      {hasError("email") && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: 12, color: "#DC2626" }}>
                          <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} />{errors.email}
                        </div>
                      )}
                      {isOk("email") && (
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: 12, color: "#16A34A" }}>
                          <i className="fa-solid fa-circle-check" style={{ fontSize: 11 }} />Looks good!
                        </div>
                      )}
                    </div>

                    <div className="col-12">
                      <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Phone</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter your phone number (optional)"
                        value={form.phone}
                        onChange={e => setField("phone", e.target.value)}
                      />
                    </div>

                    <div className="col-12">
                      <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                        Message <span style={{ color: "#DC2626" }}>*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows={5}
                        placeholder="What can we do for you?"
                        value={form.message}
                        onChange={e => setField("message", e.target.value)}
                        onBlur={() => blur("message")}
                        style={{ resize: "vertical", borderColor: hasError("message") ? "#DC2626" : isOk("message") ? "#16A34A" : "#E5E7EB", boxShadow: hasError("message") ? "0 0 0 3px rgba(239,68,68,0.1)" : isOk("message") ? "0 0 0 3px rgba(16,185,129,0.1)" : "none" }}
                      />
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 5 }}>
                        {hasError("message") ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#DC2626" }}>
                            <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 11 }} />{errors.message}
                          </div>
                        ) : <span />}
                        <span style={{ fontSize: 11, color: form.message.length > 10 ? "#16A34A" : "#9CA3AF" }}>
                          {form.message.length} chars
                        </span>
                      </div>
                    </div>

                    <div className="col-12">
                      {submitError && (
                        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"12px 14px", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10, marginBottom:14, fontSize:13, color:"#DC2626" }}>
                          <i className="fa-solid fa-circle-exclamation" />{submitError}
                        </div>
                      )}
                      <button type="submit" disabled={busy}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "14px", borderRadius: 99, background: busy ? "#9CA3AF" : "linear-gradient(135deg, #2563EB, #7C3AED)", color: "#fff", fontWeight: 700, fontSize: 15, border: "none", cursor: busy ? "not-allowed" : "pointer", fontFamily: "inherit", boxShadow: busy ? "none" : "0 6px 20px rgba(79,70,229,0.3)", transition: "all 0.2s" }}>
                        {busy
                          ? <><i className="fa-solid fa-spinner fa-spin" /> Sending...</>
                          : <><i className="fa-solid fa-paper-plane" /> Send Message</>}
                      </button>
                      <p style={{ textAlign: "center", fontSize: 12, color: "#9CA3AF", marginTop: 12, marginBottom: 0 }}>
                        We'll never share your information with third parties.
                      </p>
                    </div>

                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}